/**
 * Рендер Discord-markdown в DOM (без innerHTML - безопасно для пользовательского текста).
 *
 * Режимы:
 *  - full   - текст сообщения, описание эмбеда, значения полей, текстовые блоки V2
 *  - inline - без блочных элементов (заголовки, списки, цитаты, блоки кода)
 *  - title  - заголовки эмбедов и названия полей: форматирование без ссылок и упоминаний
 */
import { discordTimestamp } from './time.js';

export type MdMode = 'full' | 'inline' | 'title';

export interface MdCtx {
  role(id: string): { name: string; color: number } | undefined;
  channel(id: string): { name: string; type: number } | undefined;
  user(id: string): string | undefined;
  /** превращает ссылку на кастомное эмодзи в url картинки */
  emojiUrl(id: string, animated: boolean): string;
}

function el<K extends keyof HTMLElementTagNameMap>(tag: K, cls?: string, ...kids: (Node | string)[]): HTMLElementTagNameMap[K] {
  const e = document.createElement(tag);
  if (cls) e.className = cls;
  for (const k of kids) e.append(k);
  return e;
}

/* ================= Инлайн ================= */

type Rule = {
  re: RegExp;
  modes: MdMode[];
  render: (m: RegExpExecArray, mode: MdMode, ctx: MdCtx) => Node;
};

const ALL: MdMode[] = ['full', 'inline', 'title'];
const LINKS: MdMode[] = ['full', 'inline'];

function hexToRgba(n: number, a: number) {
  return `rgba(${(n >> 16) & 255}, ${(n >> 8) & 255}, ${n & 255}, ${a})`;
}

function mention(text: string, cls = '', color?: number): HTMLElement {
  const s = el('span', `md-mention ${cls}`.trim(), text);
  if (color) {
    s.style.color = `#${color.toString(16).padStart(6, '0')}`;
    s.style.background = hexToRgba(color, 0.12);
  }
  return s;
}

function safeLink(href: string, kids: Node[], title?: string): HTMLElement {
  const a = el('a', 'md-link');
  a.href = href;
  a.target = '_blank';
  a.rel = 'noreferrer noopener';
  if (title) a.title = title;
  for (const k of kids) a.append(k);
  return a;
}

const RULES: Rule[] = [
  {
    re: /^\\([^0-9A-Za-z\s])/,
    modes: ALL,
    render: (m) => document.createTextNode(m[1]),
  },
  {
    re: /^<(https?:\/\/[^\s>]+)>/,
    modes: LINKS,
    render: (m) => safeLink(m[1], [document.createTextNode(m[1])]),
  },
  {
    re: /^\[((?:\\.|[^\[\]\\]|\[[^\]]*\])+)\]\(\s*<?(https?:\/\/[^\s)>]+)>?(?:\s+"([^"]*)")?\s*\)/,
    modes: LINKS,
    render: (m, mode, ctx) => safeLink(m[2], [renderInline(m[1], mode === 'full' ? 'inline' : mode, ctx)], m[3] || m[2]),
  },
  {
    re: /^https?:\/\/[^\s<]+[^<.,:;"')\]\s]/,
    modes: LINKS,
    render: (m) => safeLink(m[0], [document.createTextNode(m[0])]),
  },
  {
    re: /^<a?:([\w~]{1,32}):(\d{15,25})>/,
    modes: ALL,
    render: (m, _mode, ctx) => {
      const img = el('img', 'md-emoji');
      const animated = m[0].startsWith('<a:');
      img.src = ctx.emojiUrl(m[2], animated);
      img.alt = `:${m[1]}:`;
      img.title = `:${m[1]}:`;
      img.draggable = false;
      img.loading = 'lazy';
      return img;
    },
  },
  {
    re: /^<t:(-?\d{1,13})(?::([tTdDfFRsS]))?>/,
    modes: ALL,
    render: (m) => {
      const span = el('span', 'md-timestamp', discordTimestamp(Number(m[1]), m[2] || 'f'));
      span.title = discordTimestamp(Number(m[1]), 'F');
      return span;
    },
  },
  {
    re: /^<@!?(\d{15,25})>/,
    modes: LINKS,
    render: (m, _mode, ctx) => mention(`@${ctx.user(m[1]) ?? 'пользователь'}`),
  },
  {
    re: /^<@&(\d{15,25})>/,
    modes: LINKS,
    render: (m, _mode, ctx) => {
      const r = ctx.role(m[1]);
      return mention(`@${r?.name ?? 'неизвестная-роль'}`, 'md-role', r?.color || undefined);
    },
  },
  {
    re: /^<#(\d{15,25})>/,
    modes: LINKS,
    render: (m, _mode, ctx) => {
      const c = ctx.channel(m[1]);
      const prefix = c && [2, 13].includes(c.type) ? '🔊 ' : c && [15, 16].includes(c.type) ? '💬 ' : '# ';
      return mention(`${prefix}${c?.name ?? 'неизвестный-канал'}`, 'md-channel');
    },
  },
  {
    re: /^<\/([-_\p{L}\p{N} ]{1,100}):(\d{15,25})>/u,
    modes: LINKS,
    render: (m) => mention(`/${m[1]}`, 'md-command'),
  },
  {
    re: /^<id:(customize|browse|guide|linked-roles)>/,
    modes: LINKS,
    render: (m) => mention(({ customize: 'Каналы и роли', browse: 'Обзор каналов', guide: 'Путеводитель по серверу', 'linked-roles': 'Связанные роли' } as Record<string, string>)[m[1]], 'md-channel'),
  },
  {
    re: /^@(everyone|here)\b/,
    modes: LINKS,
    render: (m) => mention(`@${m[1]}`),
  },
  {
    re: /^(``?)([^`]|[^`][\s\S]*?[^`])\1(?!`)/,
    modes: ALL,
    render: (m) => el('code', 'md-code-inline', m[2].trim() || m[2]),
  },
  {
    re: /^\|\|([\s\S]+?)\|\|/,
    modes: ALL,
    render: (m, mode, ctx) => {
      const s = el('span', 'md-spoiler');
      s.append(renderInline(m[1], mode, ctx));
      s.addEventListener('click', (e) => {
        e.stopPropagation();
        s.classList.add('revealed');
      });
      return s;
    },
  },
  {
    re: /^\*\*\*([\s\S]+?)\*\*\*(?!\*)/,
    modes: ALL,
    render: (m, mode, ctx) => el('strong', '', el('em', '', renderInline(m[1], mode, ctx))),
  },
  {
    re: /^\*\*([\s\S]+?)\*\*(?!\*)/,
    modes: ALL,
    render: (m, mode, ctx) => el('strong', '', renderInline(m[1], mode, ctx)),
  },
  {
    re: /^__([\s\S]+?)__(?!_)/,
    modes: ALL,
    render: (m, mode, ctx) => el('u', '', renderInline(m[1], mode, ctx)),
  },
  {
    re: /^\*(?=\S)((?:\\[\s\S]|[^\\*])+?)\*(?!\*)/,
    modes: ALL,
    render: (m, mode, ctx) => el('em', '', renderInline(m[1], mode, ctx)),
  },
  {
    re: /^_((?:__|\\[\s\S]|[^\\_])+?)_(?![\p{L}\p{N}])/u,
    modes: ALL,
    render: (m, mode, ctx) => el('em', '', renderInline(m[1], mode, ctx)),
  },
  {
    re: /^~~([\s\S]+?)~~/,
    modes: ALL,
    render: (m, mode, ctx) => el('s', '', renderInline(m[1], mode, ctx)),
  },
];

const TEXT_RE = /^[\s\S]+?(?=[\\<[*_~|`@]|https?:\/\/|$)/;

export function renderInline(text: string, mode: MdMode, ctx: MdCtx): DocumentFragment {
  const frag = document.createDocumentFragment();
  let buf = '';
  const flush = () => {
    if (buf) frag.append(document.createTextNode(buf));
    buf = '';
  };
  let i = 0;
  // в середине слова «_» не начинает курсив
  let prevChar = '';
  while (i < text.length) {
    const rest = text.slice(i);
    let matched = false;
    for (const rule of RULES) {
      if (!rule.modes.includes(mode)) continue;
      if (rule.re.source.startsWith('^_(') && /[\p{L}\p{N}]/u.test(prevChar)) continue;
      const m = rule.re.exec(rest);
      if (m && m[0].length > 0) {
        flush();
        frag.append(rule.render(m, mode, ctx));
        i += m[0].length;
        prevChar = m[0][m[0].length - 1];
        matched = true;
        break;
      }
    }
    if (matched) continue;
    const t = TEXT_RE.exec(rest);
    const chunk = t && t[0].length ? t[0] : rest[0];
    buf += chunk;
    i += chunk.length;
    prevChar = chunk[chunk.length - 1];
  }
  flush();
  return frag;
}

/* ================= Блоки ================= */

const HEADING_RE = /^(#{1,3}) +(.+)$/;
const SUBTEXT_RE = /^-# +(.+)$/;
const LIST_RE = /^( *)([-*]|\d{1,9}\.) +(.*)$/;

interface ListItem {
  indent: number;
  ordered: boolean;
  start: number;
  text: string;
}

function renderList(items: ListItem[], ctx: MdCtx): HTMLElement {
  const root = el(items[0].ordered ? 'ol' : 'ul', 'md-list');
  if (items[0].ordered && items[0].start !== 1) (root as HTMLOListElement).start = items[0].start;
  const stack: { indent: number; list: HTMLElement; last: HTMLElement | null }[] = [{ indent: items[0].indent, list: root, last: null }];
  for (const it of items) {
    let top = stack[stack.length - 1];
    if (it.indent > top.indent + 1 && top.last && stack.length < 11) {
      const nested = el(it.ordered ? 'ol' : 'ul', 'md-list');
      if (it.ordered && it.start !== 1) (nested as HTMLOListElement).start = it.start;
      top.last.append(nested);
      stack.push({ indent: it.indent, list: nested, last: null });
      top = stack[stack.length - 1];
    } else {
      while (stack.length > 1 && it.indent < top.indent) {
        stack.pop();
        top = stack[stack.length - 1];
      }
    }
    const li = el('li', '');
    li.append(renderInline(it.text, 'full', ctx));
    top.list.append(li);
    top.last = li;
  }
  return root;
}

function renderLines(lines: string[], ctx: MdCtx, allowQuote: boolean, out: HTMLElement) {
  let run: string[] = [];
  const flushRun = () => {
    if (!run.length) return;
    if (run.every((l) => l === '')) {
      // пустые строки между блоками Discord сохраняет
      for (let k = 0; k < run.length; k++) out.append(el('br'));
      run = [];
      return;
    }
    const span = el('span', 'md-text');
    span.append(renderInline(run.join('\n'), 'full', ctx));
    out.append(span);
    run = [];
  };

  for (let i = 0; i < lines.length; i++) {
    const line = lines[i];

    if (allowQuote && /^>>> ?/.test(line)) {
      flushRun();
      const rest = [line.replace(/^>>> ?/, ''), ...lines.slice(i + 1)];
      const bq = el('blockquote', 'md-quote');
      renderLines(rest, ctx, false, bq);
      out.append(bq);
      return;
    }
    if (allowQuote && /^> /.test(line)) {
      flushRun();
      const quoted: string[] = [];
      while (i < lines.length && /^> /.test(lines[i])) {
        quoted.push(lines[i].slice(2));
        i++;
      }
      i--;
      const bq = el('blockquote', 'md-quote');
      renderLines(quoted, ctx, false, bq);
      out.append(bq);
      continue;
    }

    const hm = HEADING_RE.exec(line);
    if (hm) {
      flushRun();
      const lvl = hm[1].length as 1 | 2 | 3;
      const hEl = el(`h${lvl}` as 'h1', `md-h md-h${lvl}`);
      hEl.append(renderInline(hm[2], 'full', ctx));
      out.append(hEl);
      continue;
    }

    const sm = SUBTEXT_RE.exec(line);
    if (sm) {
      flushRun();
      const small = el('div', 'md-subtext');
      small.append(renderInline(sm[1], 'full', ctx));
      out.append(small);
      continue;
    }

    const lm = LIST_RE.exec(line);
    if (lm) {
      flushRun();
      const items: ListItem[] = [];
      while (i < lines.length) {
        const m = LIST_RE.exec(lines[i]);
        if (!m) break;
        const ordered = /\d/.test(m[2]);
        // смена типа списка на верхнем уровне начинает новый список
        if (items.length && m[1].length <= items[0].indent && ordered !== items[0].ordered) break;
        items.push({ indent: m[1].length, ordered, start: ordered ? parseInt(m[2], 10) : 1, text: m[3] });
        i++;
      }
      i--;
      out.append(renderList(items, ctx));
      continue;
    }

    run.push(line);
  }
  flushRun();
}

const CODE_BLOCK_RE = /```(?:([^\n`]*)\n)?([\s\S]*?)```/g;

export function renderMarkdown(text: string, mode: MdMode, ctx: MdCtx): HTMLElement {
  const root = el('div', `md md-${mode}`);
  if (!text) return root;
  if (mode !== 'full') {
    root.append(renderInline(mode === 'title' ? text.replace(/\n/g, ' ') : text, mode, ctx));
    return root;
  }

  let last = 0;
  CODE_BLOCK_RE.lastIndex = 0;
  let m: RegExpExecArray | null;
  const segments: { code: boolean; text: string; lang?: string }[] = [];
  while ((m = CODE_BLOCK_RE.exec(text))) {
    if (m.index > last) segments.push({ code: false, text: text.slice(last, m.index) });
    let lang = (m[1] ?? '').trim();
    let body = m[2];
    if (m[1] !== undefined && /\s/.test(lang)) {
      // первая строка с пробелами - это не язык, а код
      body = `${m[1]}\n${body}`;
      lang = '';
    }
    segments.push({ code: true, text: body.replace(/\n$/, ''), lang });
    last = m.index + m[0].length;
  }
  if (last < text.length) segments.push({ code: false, text: text.slice(last) });

  for (const seg of segments) {
    if (seg.code) {
      const pre = el('pre', 'md-codeblock');
      const code = el('code', seg.lang ? `lang-${seg.lang}` : '', seg.text);
      pre.append(code);
      root.append(pre);
    } else {
      // перевод строки вокруг блока кода Discord «съедает»
      const t = seg.text.replace(/^\n/, '').replace(/\n$/, '');
      if (t === '' && seg.text !== '') continue;
      renderLines(t.split('\n'), ctx, true, root);
    }
  }
  return root;
}

/* ================= Эмодзи-only (большие эмодзи) ================= */

const EMOJI_ONLY_RE = /^(?:\s|<a?:[\w~]{1,32}:\d{15,25}>|\p{Extended_Pictographic}|\p{Emoji_Component}|‍|️)+$/u;

export function isJumbo(text: string): boolean {
  const t = text.trim();
  if (!t || !EMOJI_ONLY_RE.test(t)) return false;
  if (/^[\d#*\s]+$/.test(t)) return false;
  const custom = (t.match(/<a?:[\w~]+:\d+>/g) ?? []).length;
  const rest = t.replace(/<a?:[\w~]+:\d+>/g, '').replace(/\s/g, '');
  const uni = [...new Intl.Segmenter().segment(rest)].length;
  return custom + uni > 0 && custom + uni <= 30;
}
